import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, ConfirmDialogService, ToastService } from '@dental-ui/ui';
import {
  AttachmentApiService,
  DocumentTypeEnum,
  FileTypeEnum,
} from '@dental-ui/attachment-service';
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideImage,
  LucidePencil,
  LucidePlus,
  LucideRefreshCw,
  LucideSearch,
  LucideTrash2,
  LucideX,
} from '@lucide/angular';
import {
  MachineModelDto,
  PortalMachineModelsApiService,
  SelectOption,
} from '../data-access/portal-machine-models-api.service';

const PAGE_SIZE = 10;

@Component({
  selector: 'lib-portal-machine-models',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    LucideChevronLeft,
    LucideChevronRight,
    LucideImage,
    LucidePencil,
    LucidePlus,
    LucideRefreshCw,
    LucideSearch,
    LucideTrash2,
    LucideX,
  ],
  templateUrl: './portal-machine-models.component.html',
  styleUrl: './portal-machine-models.component.scss',
})
export class PortalMachineModelsComponent implements OnInit {
  private readonly api = inject(PortalMachineModelsApiService);
  private readonly attachmentApi = inject(AttachmentApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<MachineModelDto[]>([]);
  readonly loading = signal(false);
  readonly totalPages = signal(0);
  readonly page = signal(0);

  searchName = '';
  searchBrand = '';
  searchMachineType = '';

  readonly showFormDialog = signal(false);
  readonly formSaving = signal(false);
  readonly isEdit = signal(false);
  readonly formModel = signal<MachineModelDto>(this.emptyModel());

  readonly brandOptions = signal<SelectOption[]>([]);
  readonly machineTypeOptions = signal<SelectOption[]>([]);
  readonly optionsLoading = signal(false);

  readonly showImageDialog = signal(false);
  readonly selectedModel = signal<MachineModelDto | null>(null);
  readonly imageSaving = signal(false);
  selectedImageFile: File | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api
      .getMachineModels(this.page(), PAGE_SIZE, {
        name: this.searchName,
        brand: this.searchBrand,
        machineType: this.searchMachineType,
      })
      .subscribe({
        next: (res) => {
          this.items.set(res.data);
          this.totalPages.set(res.totalPage);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toast.error('Makine modelleri yüklenirken hata oluştu');
        },
      });
  }

  onSearch(): void {
    this.page.set(0);
    this.load();
  }

  clearSearch(): void {
    this.searchName = '';
    this.searchBrand = '';
    this.searchMachineType = '';
    this.page.set(0);
    this.load();
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.load();
    }
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) {
      this.page.update((p) => p + 1);
      this.load();
    }
  }

  openAdd(): void {
    this.loadFormOptions();
    this.isEdit.set(false);
    this.formModel.set(this.emptyModel());
    this.showFormDialog.set(true);
  }

  openEdit(item: MachineModelDto): void {
    this.loadFormOptions();
    this.isEdit.set(true);
    this.formModel.set({ ...item });
    this.showFormDialog.set(true);
  }

  closeFormDialog(): void {
    this.showFormDialog.set(false);
  }

  updateField<K extends keyof MachineModelDto>(key: K, value: MachineModelDto[K]): void {
    this.formModel.update((prev) => ({ ...prev, [key]: value }));
  }

  save(): void {
    const model = this.formModel();
    if (!model.name.trim()) {
      this.toast.error('Ad alanı zorunludur');
      return;
    }
    if (!model.machineTypeId) {
      this.toast.error('Makine türü seçiniz');
      return;
    }
    if (!model.brandId) {
      this.toast.error('Marka seçiniz');
      return;
    }

    this.formSaving.set(true);
    const payload = {
      id: model.id,
      name: model.name,
      machineTypeId: model.machineTypeId,
      brandId: model.brandId,
      isActive: model.isActive,
    };
    const call = this.isEdit()
      ? this.api.updateMachineModel({ ...model, ...payload })
      : this.api.createMachineModel(payload);

    call.subscribe({
      next: () => {
        this.formSaving.set(false);
        this.toast.success(this.isEdit() ? 'Makine modeli güncellendi' : 'Makine modeli eklendi');
        this.showFormDialog.set(false);
        this.load();
      },
      error: () => {
        this.formSaving.set(false);
        this.toast.error('İşlem sırasında hata oluştu');
      },
    });
  }

  delete(item: MachineModelDto): void {
    this.confirmDialog
      .confirm({
        title: 'Makine Modelini Sil',
        message: `"${item.name}" kaydını silmek istediğinizden emin misiniz?`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deleteMachineModel(item.id).subscribe({
          next: () => {
            this.toast.success('Makine modeli silindi');
            this.load();
          },
          error: () => this.toast.error('Silme işlemi başarısız'),
        });
      });
  }

  openImageDialog(item: MachineModelDto): void {
    this.selectedModel.set(item);
    this.selectedImageFile = null;
    this.showImageDialog.set(true);
  }

  closeImageDialog(): void {
    this.showImageDialog.set(false);
    this.selectedImageFile = null;
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedImageFile = input.files?.[0] ?? null;
  }

  saveImage(): void {
    const model = this.selectedModel();
    if (!model || !this.selectedImageFile) {
      this.toast.error('Lütfen bir görsel seçiniz');
      return;
    }

    this.imageSaving.set(true);
    this.attachmentApi
      .uploadFile(
        this.selectedImageFile,
        { name: 'machineModelId', value: model.id },
        FileTypeEnum.MachineModelImage,
        DocumentTypeEnum.Other,
        false
      )
      .subscribe({
        next: () => {
          this.imageSaving.set(false);
          this.toast.success('Görsel yüklendi');
          this.closeImageDialog();
          this.load();
        },
        error: () => {
          this.imageSaving.set(false);
          this.toast.error('Görsel yüklenirken hata oluştu');
        },
      });
  }

  deleteImage(): void {
    const model = this.selectedModel();
    const attachmentId = this.extractAttachmentId(model?.fileUrl ?? null);
    if (!attachmentId) {
      this.toast.error('Silinecek görsel bulunamadı');
      return;
    }

    this.imageSaving.set(true);
    this.attachmentApi.deleteAttachment(attachmentId).subscribe({
      next: () => {
        this.imageSaving.set(false);
        this.toast.success('Görsel silindi');
        this.closeImageDialog();
        this.load();
      },
      error: () => {
        this.imageSaving.set(false);
        this.toast.error('Görsel silinirken hata oluştu');
      },
    });
  }

  private loadFormOptions(): void {
    this.optionsLoading.set(true);
    this.api.getAllBrands().subscribe({
      next: (brands) => this.brandOptions.set(brands),
      error: () => this.toast.error('Markalar yüklenirken hata oluştu'),
    });
    this.api.getAllMachineTypes().subscribe({
      next: (types) => {
        this.machineTypeOptions.set(types);
        this.optionsLoading.set(false);
      },
      error: () => {
        this.optionsLoading.set(false);
        this.toast.error('Makine türleri yüklenirken hata oluştu');
      },
    });
  }

  private emptyModel(): MachineModelDto {
    return {
      id: '',
      name: '',
      machineTypeId: '',
      machineType: '',
      brandId: '',
      brand: '',
      fileUrl: null,
      isActive: true,
    };
  }

  private extractAttachmentId(fileUrl: string | null): string | null {
    if (!fileUrl) return null;
    const fileName = fileUrl.split('/').pop();
    if (!fileName) return null;
    return fileName.replace(/\.[^.]+$/, '');
  }
}
